import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { previewPages } from '../data/previews';
import { productShortName } from '../data/offer';
export function LookInsideCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = previewPages.length;
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (index + total) % total;
    const slide = track.children[next] as HTMLElement | undefined;
    if (!slide) return;
    track.scrollTo({
      left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
      behavior: 'smooth'
    });
  }, [total]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let distance = Infinity;
      Array.from(track.children).forEach((child, index) => {
        const el = child as HTMLElement;
        const d = Math.abs(el.offsetLeft + el.clientWidth / 2 - center);
        if (d < distance) {
          distance = d;
          closest = index;
        }
      });
      setActive(closest);
    };
    onScroll();
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);
  const arrowClass = 'absolute top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-slate-700 bg-slate-900 text-white shadow-sm transition-colors duration-150 ease-out hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400';
  return <section className="bg-slate-950 py-16 sm:py-20" aria-labelledby="inside-title" data-id="element-95" data-mp="3vp0fi0fivcxv">
      <div className="mx-auto max-w-6xl px-4" data-id="element-96" data-mp="3vmmpw73m2sxp">
        <div className="text-center" data-id="element-97" data-mp="3qqyyrm5kjppk">
          <p className="text-sm font-semibold text-orange-400" data-id="element-98" data-mp="3qnt8w4p48jss">Look Inside</p>
          <h2 id="inside-title" className="mx-auto mt-3 max-w-4xl text-balance text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-99" data-mp="1oz5jp2j5ladn">
            Discover What&apos;s Inside <span className="text-amber-400" data-id="element-100" data-mp="1f3lqinvz4psl">{productShortName}</span>
          </h2>
          <p className="mx-auto mt-6 max-w-3xl leading-relaxed text-slate-300" data-id="element-101" data-mp="1k13xw9ucewyh">
            Visual pages with clear mine layouts, cross-sections, worked examples, technical warnings and field-ready
            takeaways. Here are some real pages from Volume 1.
          </p>
        </div>

        <div className="relative mt-10" role="region" aria-roledescription="carousel" aria-label="Sample pages" data-id="element-102" data-mp="1yqtdvb73h5it">
          <div ref={trackRef} className="flex touch-pan-x snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain scroll-smooth px-[10%] pb-2 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] sm:px-[20%] md:px-[35%] [&::-webkit-scrollbar]:hidden" style={{
          touchAction: 'pan-x pan-y'
        }} data-id="element-103" data-mp="22rdeioqvmr9p">
            {previewPages.map((page, index) => <figure key={page.src} className={`w-[80%] shrink-0 snap-center overflow-hidden rounded-xl border bg-slate-900 shadow-lg shadow-black/40 transition-[border-color,opacity] duration-200 ease-out sm:w-[60%] md:w-[30%] ${index === active ? 'border-amber-400/60 opacity-100' : 'border-slate-800 opacity-60'}`} aria-roledescription="slide" aria-label={`${index + 1} of ${total}`} data-id="element-104" data-mp="jv9zvnwqx09k" data-mpi={index}>
                <img src={page.src} alt={page.alt} loading="lazy" draggable={false} className="block aspect-[4/5] w-full select-none object-cover" data-id="element-105" data-mp="10v0vyafp7lq" />
              </figure>)}
          </div>

          <button type="button" onClick={() => goTo(active - 1)} className={`${arrowClass} left-0 sm:-left-2`} aria-label="Previous page" data-id="element-106" data-mp="au3h186mjdd5">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-107" data-mp="3ehlx8j2az114" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} className={`${arrowClass} right-0 sm:-right-2`} aria-label="Next page" data-id="element-108" data-mp="39isjcpj3t80f">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-109" data-mp="2csf9lpaave30" />
          </button>
        </div>

        <div className="mt-6 flex justify-center gap-2" data-id="element-110" data-mp="2gu0spsdfgoln">
          {previewPages.map((page, index) => <button key={page.src} type="button" onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 ${index === active ? 'w-6 bg-amber-400' : 'w-2 bg-slate-700 hover:bg-slate-600'}`} aria-label={`Go to page ${index + 1}`} aria-current={index === active} data-id="element-111" data-mp="2lru20ua11yq5" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}